import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { makeStyles } from '@material-ui/core/styles';

import Button from '@bequestinc/wui/input/button';
import Form from '@bequestinc/wui/layout/form';
import Spacer from '@bequestinc/wui/layout/spacer';
import Typography from '@bequestinc/wui/basics/typography';
import Box from '@material-ui/core/Box';
import Textbox from '@bequestinc/wui/input/textbox';

import PasswordField from 'components/PasswordField';
import TermsCheckbox from 'components/TermsCheckbox';
import PasswordHelp from 'components/PasswordHelp';
import { useGlobalContext, useInputFieldState } from 'hooks';
import { UNKNOWN_ERROR } from 'utils/constants/errors';
import { validateEmail, validatePassword } from 'utils/validation';
import env from 'utils/env';

const useStyles = makeStyles({
  form: {
    maxWidth: 550,
  },
});

const AuthBase = ({
  submitCredentials,
  submitText,
  headerText,
  showTerms,
  confirmPassword,
  referer,
  isRegisterPage,
  children,
}) => {
  const [email, onChangeEmail] = useInputFieldState('');
  const [password, setPassword] = useState('');
  const [termsOfService, setTermsOfService] = useState(false);
  const [inputErrors, setInputErrors] = useState({});
  const [processing, setProcessing] = useState(false);
  const didSucceed = useRef(false);
  const [loading, setLoading] = useState(false);
  const [showMembersLabel, setShowMembersLabel] = useState(false);
  const router = useRouter();
  const {
    query,
    query: { next, home },
  } = router;
  const store = useGlobalContext();
  const classes = useStyles();

  const onChangePassword = event => {
    const inputedPassword = event.target.value;
    setPassword(inputedPassword);

    if (confirmPassword) {
      const passwordError = validatePassword(email, inputedPassword);
      setInputErrors(prevErrors => ({ ...prevErrors, password: passwordError }));
    }
  };

  useEffect(() => {
    try {
      const blockedSources = ['sponsors', 'attorneys', 'wills'];
      const sourceUrl = home || next || '';
      const url = new URL(sourceUrl, window.location.origin);
      const hostname = url.hostname.toLowerCase();

      const isBlockedSource = blockedSources.some(source => hostname.includes(source));

      if (!isBlockedSource) {
        setShowMembersLabel(true);
      }
    } catch {
      setShowMembersLabel(true);
    }
  }, [home, next]);

  useEffect(() => {
    if (!didSucceed.current && store.authenticated) {
      // In general, we should link to the correct app, not directly to login/register page. If we have a link to the
      // login or register page, and the user is already authenticated, send them to the member app by default.
      setLoading(true);
      env.membersAppUrl.then(url => router.push(url));
    }
  }, [store.authenticated, router, setLoading]);

  const validateForm = () => {
    const errors = {};

    if (!email) {
      errors.email = 'Please enter your email';
    } else {
      const emailError = validateEmail(email);
      if (emailError) {
        errors.email = emailError;
      }
    }

    const passwordError = validatePassword(email, password);
    if (!password) {
      errors.password = 'Please enter your password.';
    } else if (confirmPassword && passwordError) {
      errors.password = passwordError;
    }

    if (showTerms && !termsOfService) {
      errors.terms = 'Please agree to continue.';
    }

    setInputErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSuccess = response => {
    didSucceed.current = true;
    if (response.data.mfaRequired) {
      store.ephemeralMfaToken = response.data.ephemeralToken;
      store.mfaMethod = response.data.mfaMethod;
      store.loginEmail = email;
      router.push({ pathname: '/login/mfa', query });
      return;
    }
    if (response.data.passwordResetRequired) {
      store.ephemeralExpiredPasswordToken = response.data.ephemeralToken;
      store.loginEmail = email;
      router.push({ pathname: '/login/reset-password', query });
      return;
    }

    window.location.href = response.data.next;
  };

  const handleError = error => {
    if (error.response && error.response.data) {
      setInputErrors(error.response.data);
    } else {
      setInputErrors({ nonFieldErrors: UNKNOWN_ERROR });
    }
    setProcessing(false);
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (processing || !validateForm()) {
      return;
    }

    setProcessing(true);
    submitCredentials({
      register_source: referer,
      email: email.toLowerCase(),
      password,
      next,
    })
      .then(handleSuccess)
      .catch(handleError);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Typography variant="h4" component="h1">
        {headerText}
      </Typography>
      {showMembersLabel && isRegisterPage && (
        <>
          <Spacer v={8} />
          <Box sx={{ maxWidth: 550 }}>
            <Typography variant="caption" gutterBottom>
              For members, we recommend using a personal email address to ensure continuous access
              to your account at your workplace and beyond.
            </Typography>
          </Box>
          <Spacer v={8} />
        </>
      )}
      <Form
        error={inputErrors.nonFieldErrors}
        onSubmit={handleSubmit}
        noMargin
        className={classes.form}
        noValidate
      >
        <Textbox
          name="email"
          type="email"
          label="Email"
          autoComplete="email"
          value={email}
          onChange={onChangeEmail}
          error={inputErrors.email}
        />
        <PasswordField value={password} onChange={onChangePassword} error={inputErrors.password} />
        {showTerms && (
          <>
            <PasswordHelp password={password} userInputs={[email]} />
            <TermsCheckbox
              onChange={() => setTermsOfService(!termsOfService)}
              error={inputErrors.terms}
            />
          </>
        )}
        <Spacer v={8} />
        <Button
          variant="contained"
          color="primary"
          type="submit"
          noMinWidth
          size="large"
          disabled={processing}
        >
          {submitText}
        </Button>
      </Form>

      <Spacer v={16} />

      {children}

      <Spacer v={24} />
    </>
  );
};

AuthBase.propTypes = {
  submitCredentials: PropTypes.func.isRequired,
  submitText: PropTypes.string.isRequired,
  headerText: PropTypes.string.isRequired,
  showTerms: PropTypes.bool,
  confirmPassword: PropTypes.bool,
  referer: PropTypes.string,
  isRegisterPage: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

AuthBase.defaultProps = {
  showTerms: false,
  confirmPassword: false,
  referer: '',
  isRegisterPage: false,
};

export default observer(AuthBase);
